import { 
  ref,
  uploadBytes,
  listAll,
  getDownloadURL,
  getMetadata,
  updateMetadata
} from 'firebase/storage';
import { 
  doc, 
  runTransaction 
} from 'firebase/firestore';
import { storage, db } from '../config/firebase';
import type { Quote } from '../types/quotes';
import { quoteService } from './quoteService';
import { pdfService } from './pdfService';

export interface InvoiceMetadata {
  invoiceNumber: string;
  quoteId: string;
  storagePath: string;
  downloadURL?: string;
  createdAt: Date;
  clientName: string;
  projectName?: string;
  amountTotal: number;
  status?: 'active' | 'void';
  voidReason?: string;
  voidedAt?: Date;
  voidedBy?: string;
  voidedByName?: string;
}

export const invoiceService = {
  /**
   * Generate invoice number using atomic Firestore counter (e.g., RE-2025-001)
   * Prevents race conditions when multiple admins create invoices simultaneously
   * @returns Invoice number string
   */
  async generateInvoiceNumber(): Promise<string> {
    const year = new Date().getFullYear();
    const prefix = `RE-${year}-`;
    const counterDocRef = doc(db, 'counters', `invoices-${year}`);
    
    try {
      // Use Firestore transaction to atomically increment counter
      const invoiceNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterDocRef);
        
        let nextNumber: number;
        
        if (!counterDoc.exists()) {
          // First invoice of the year - initialize counter
          nextNumber = 1;
          transaction.set(counterDocRef, {
            count: nextNumber,
            year: year,
            createdAt: new Date(),
            updatedAt: new Date()
          });
        } else {
          // Increment existing counter
          const currentCount = counterDoc.data()?.count || 0;
          nextNumber = currentCount + 1;
          transaction.update(counterDocRef, {
            count: nextNumber,
            updatedAt: new Date()
          });
        }
        
        // Format: RE-2025-001
        const formattedNumber = `${prefix}${nextNumber.toString().padStart(3, '0')}`;
        return formattedNumber;
      });
      
      console.log('✅ Generated invoice number:', invoiceNumber);
      return invoiceNumber;
      
    } catch (error) {
      console.error('❌ Error generating invoice number:', error);
      // Fallback to timestamp-based number to avoid blocking invoice creation
      const timestamp = Date.now().toString().slice(-6);
      const fallbackNumber = `${prefix}T${timestamp}`;
      console.warn('⚠️ Using fallback invoice number:', fallbackNumber);
      return fallbackNumber;
    }
  },

  /**
   * Create invoice from completed quote - uploads PDF to Storage
   * @param quote Completed quote
   * @param createdByUserId User ID creating the invoice
   * @param createdByUserName User name creating the invoice
   * @returns Invoice number
   */
  async createInvoiceFromQuote(
    quote: Quote,
    createdByUserId: string,
    createdByUserName: string
  ): Promise<string> {
    try {
      // Check if invoice already exists for this quote
      const invoicesRef = ref(storage, 'invoices');
      try {
        const files = await listAll(invoicesRef);
        // Check if any file contains the quote ID in metadata or name
        for (const file of files.items) {
          const metadata = await getMetadata(file);
          if (metadata.customMetadata?.quoteId === quote.id) {
            // Invoice already exists
            console.log(`Invoice already exists for quote ${quote.id}: ${metadata.customMetadata.invoiceNumber}`);
            return metadata.customMetadata.invoiceNumber || file.name.replace('.pdf', '');
          }
        }
      } catch (error) {
        // Folder might not exist yet, that's ok
        console.log('Checking existing invoices (folder might not exist):', error);
      }
      
      // Generate invoice number
      console.log(`Generating invoice number for quote ${quote.id}...`);
      const invoiceNumber = await this.generateInvoiceNumber();
      console.log(`Generated invoice number: ${invoiceNumber}`);
      
      const issuedAt = new Date();
      
      // Generate PDF
      console.log(`Generating PDF for invoice ${invoiceNumber}...`);
      const pdfBlob = pdfService.generateInvoicePDF(quote, invoiceNumber);
      console.log(`PDF generated, size: ${pdfBlob.size} bytes`);
      
      // Upload to Storage
      const storagePath = `invoices/${invoiceNumber}.pdf`;
      const storageRef = ref(storage, storagePath);
      
      console.log(`Uploading invoice to Storage: ${storagePath}...`);
      await uploadBytes(storageRef, pdfBlob, {
        contentType: 'application/pdf',
        customMetadata: {
          quoteId: quote.id,
          invoiceNumber: invoiceNumber,
          clientName: quote.clientName,
          projectName: quote.projectName || '',
          amountTotal: quote.amountTotal.toString(),
          createdAt: issuedAt.toISOString(),
          createdByUserId: createdByUserId,
          createdByUserName: createdByUserName
        }
      });
      
      console.log(`✅ Invoice ${invoiceNumber} successfully uploaded to Storage`);
      return invoiceNumber;
    } catch (error) {
      console.error(`❌ Failed to create invoice for quote ${quote.id}:`, error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      throw error; // Re-throw to let caller handle it
    }
  },

  /**
   * List all invoices from Storage
   * @returns Array of invoice metadata
   */
  async listInvoices(): Promise<InvoiceMetadata[]> {
    try {
      const invoicesRef = ref(storage, 'invoices');
      const files = await listAll(invoicesRef);
      
      // If no files exist, return empty array
      if (files.items.length === 0) {
        return [];
      }
      
      // Get metadata for each file
      const invoices: InvoiceMetadata[] = [];
      
      for (const file of files.items) {
        try {
          // Only process PDF files
          if (!file.name.endsWith('.pdf')) {
            continue;
          }
          
          const metadata = await getMetadata(file);
          const customMetadata = metadata.customMetadata || {};
          
          // Extract invoice number from filename
          const invoiceNumber = file.name.replace('.pdf', '');
          
          // Get download URL
          const downloadURL = await getDownloadURL(file);
          
          invoices.push({
            invoiceNumber,
            quoteId: customMetadata.quoteId || '',
            storagePath: file.fullPath,
            downloadURL,
            createdAt: metadata.timeCreated ? new Date(metadata.timeCreated) : new Date(),
            clientName: customMetadata.clientName || 'Unbekannt',
            projectName: customMetadata.projectName || undefined,
            amountTotal: parseFloat(customMetadata.amountTotal || '0'),
            status: (customMetadata.status as 'active' | 'void') || 'active',
            voidReason: customMetadata.voidReason,
            voidedAt: customMetadata.voidedAt ? new Date(customMetadata.voidedAt) : undefined,
            voidedBy: customMetadata.voidedBy,
            voidedByName: customMetadata.voidedByName
          });
        } catch (error) {
          console.error(`Error processing invoice ${file.name}:`, error);
        }
      }
      
      // Sort by date (descending - newest first, oldest last)
      invoices.sort((a, b) => {
        return b.createdAt.getTime() - a.createdAt.getTime();
      });
      
      return invoices;
    } catch (error) {
      // If folder doesn't exist yet, that's ok - return empty array
      if (error instanceof Error && error.message.includes('does not exist')) {
        console.log('Invoices folder does not exist yet');
        return [];
      }
      console.error('Error listing invoices:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }
      return [];
    }
  },

  /**
   * Get invoice by invoice number
   * @param invoiceNumber Invoice number (e.g., RE-2025-001)
   * @returns Invoice metadata or null if not found
   */
  async getInvoice(invoiceNumber: string): Promise<InvoiceMetadata | null> {
    try {
      const storagePath = `invoices/${invoiceNumber}.pdf`;
      const storageRef = ref(storage, storagePath);
      
      const metadata = await getMetadata(storageRef);
      const downloadURL = await getDownloadURL(storageRef);
      const customMetadata = metadata.customMetadata || {};
      
      return {
        invoiceNumber,
        quoteId: customMetadata.quoteId || '',
        storagePath,
        downloadURL,
        createdAt: metadata.timeCreated ? new Date(metadata.timeCreated) : new Date(),
        clientName: customMetadata.clientName || 'Unbekannt',
        projectName: customMetadata.projectName || undefined,
        amountTotal: parseFloat(customMetadata.amountTotal || '0'),
        status: (customMetadata.status as 'active' | 'void') || 'active',
        voidReason: customMetadata.voidReason,
        voidedAt: customMetadata.voidedAt ? new Date(customMetadata.voidedAt) : undefined,
        voidedBy: customMetadata.voidedBy,
        voidedByName: customMetadata.voidedByName
      };
    } catch (error) {
      console.error('Error getting invoice:', error);
      return null;
    }
  },

  /**
   * Void an invoice (mark as void without deleting)
   * Maintains numbering sequence and audit trail
   * @param invoiceNumber Invoice number (e.g., RE-2025-001)
   * @param reason Reason for voiding
   * @param voidedByUserId User ID who voided the invoice
   * @param voidedByUserName User name who voided the invoice
   */
  async voidInvoice(
    invoiceNumber: string,
    reason: string,
    voidedByUserId: string,
    voidedByUserName: string
  ): Promise<void> {
    try {
      const storagePath = `invoices/${invoiceNumber}.pdf`;
      const storageRef = ref(storage, storagePath);
      
      // Get current metadata
      const currentMetadata = await getMetadata(storageRef);
      
      // Update metadata without deleting file
      await updateMetadata(storageRef, {
        customMetadata: {
          ...currentMetadata.customMetadata,
          status: 'void',
          voidReason: reason,
          voidedAt: new Date().toISOString(),
          voidedBy: voidedByUserId,
          voidedByName: voidedByUserName
        }
      });
      
      console.log(`✅ Invoice ${invoiceNumber} voided successfully`);
    } catch (error) {
      console.error(`❌ Error voiding invoice ${invoiceNumber}:`, error);
      throw error;
    }
  },

  /**
   * Create invoices for all completed quotes that don't have an invoice yet
   * This is a backfill/migration function
   * @param createdByUserId User ID creating the invoices
   * @param createdByUserName User name creating the invoices
   * @returns Number of invoices created
   */
  async createInvoicesForCompletedQuotes(
    createdByUserId: string,
    createdByUserName: string
  ): Promise<number> {
    try {
      console.log('Fetching completed quotes...');
      // Get all completed quotes
      const { quotes } = await quoteService.listQuotes({ status: ['completed'] });
      console.log(`Found ${quotes.length} completed quotes`);
      
      if (quotes.length === 0) {
        console.log('No completed quotes found');
        return 0;
      }
      
      // Get all existing invoices to check which quotes already have invoices
      console.log('Checking existing invoices...');
      let existingInvoices: InvoiceMetadata[] = [];
      try {
        existingInvoices = await this.listInvoices();
      } catch (error) {
        console.log('No existing invoices found (folder might not exist yet)');
      }
      const quotesWithInvoices = new Set(
        existingInvoices.map(inv => inv.quoteId).filter(id => id)
      );
      console.log(`Found ${quotesWithInvoices.size} existing invoices`);
      
      // Create invoices for quotes that don't have one yet
      let createdCount = 0;
      let errorCount = 0;
      for (const quote of quotes) {
        if (!quotesWithInvoices.has(quote.id)) {
          try {
            console.log(`Creating invoice for quote ${quote.id} (${quote.clientName})...`);
            const invoiceNumber = await this.createInvoiceFromQuote(quote, createdByUserId, createdByUserName);
            createdCount++;
            console.log(`✅ Invoice ${invoiceNumber} created for quote ${quote.id}`);
          } catch (error) {
            errorCount++;
            console.error(`❌ Error creating invoice for quote ${quote.id}:`, error);
            if (error instanceof Error) {
              console.error('Error message:', error.message);
              console.error('Error stack:', error.stack);
            }
            // Continue with next quote even if one fails
          }
        } else {
          console.log(`Quote ${quote.id} already has an invoice, skipping`);
        }
      }
      
      console.log(`Invoice creation summary: ${createdCount} created, ${errorCount} errors`);
      
      return createdCount;
    } catch (error) {
      console.error('❌ Error creating invoices for completed quotes:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message, error.stack);
      }
      return 0;
    }
  }
};
